import { useCallback } from "react";

import { useMedplum } from "@medplum/react";

import { fetchProblemsBadges } from "@/clients/zod-schemas/problems";
import { useAppDispatch } from "@/store";
import { loadProblems } from "@/store/slices/client-problems";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

/**
 * Re-reads the client's problem list into the store, for the surfaces that can change it from outside
 * PAM.
 *
 * The client side panel polls the same search every few seconds, but its merge keeps a problem the
 * poll has stopped returning — deliberately, so a visibility toggle saved a moment ago is not
 * overwritten by a response that predates it. That means additions reach the panel on their own and
 * **removals never do**, so anything that takes a problem off the list has to say so.
 *
 * `loadProblems` replaces the set rather than merging into it, which is what makes a removal land.
 * A failed refresh is reported and otherwise left alone: the write it follows has already succeeded,
 * and the panel is stale rather than wrong.
 */
export function useClientProblemsRefresh(clientId: string | undefined): () => Promise<void> {
  const medplum = useMedplum();
  const dispatch = useAppDispatch();

  return useCallback(async () => {
    if (!clientId) return;

    try {
      // The search is cached, and a write made through a transaction bundle does not invalidate it,
      // so without this the refresh reads the problem list as it was before the write.
      medplum.invalidateSearches("Condition");

      dispatch(loadProblems(await fetchProblemsBadges(medplum, clientId)));
    } catch (error) {
      // Only the client's id reaches Sentry. What they are diagnosed with is clinical; failing to
      // refresh the list of it is an operational problem.
      captureExceptionWithoutPhi("Failed to refresh the client's problem list", error, {
        clientId,
      });
    }
  }, [clientId, dispatch, medplum]);
}
